import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API } from "../App";
import { computeDeviceFingerprint } from "../utils/deviceFingerprint";
import { login as authLogin, register as authRegister, storeTokens } from "../utils/auth";
import { resolveLoginDestination } from "../utils/onboardingTrigger";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Heart, Mail, Lock, User, ArrowLeft, Brain, Zap, ShieldAlert, Key, Phone, FileText, CheckSquare, Share2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import { useTheme } from "../contexts/ThemeContext";
import SacredEmblem from "../components/rewards/shared/SacredEmblem";
import axios from "axios";

// ==================== T&C DOCUMENT (v1.0) ====================
// IMPORTANT: This T&C content is duplicated in /app/frontend/src/pages/TermsPage.jsx
// Any changes to the terms here MUST also be updated in TermsPage.jsx and vice versa.
const TC_VERSION = "1.0";

const generateTcDocument = ({ fullLegalName, email, phoneNumber }) => {
  const now = new Date();
  const dateStr = now.toLocaleDateString("en-AU", { year: "numeric", month: "long", day: "numeric" });
  const timestampStr = now.toISOString();

  return `**AGENTEROS.AI**

BETA PROGRAM

**NON-DISCLOSURE AGREEMENT**

&

**BETA TERMS OF USE**

Effective Date: ${dateStr}

Version ${TC_VERSION}

*This document constitutes a legally binding agreement.*

*Please read carefully before accepting.*

## PREAMBLE

This Non-Disclosure Agreement and Beta Terms of Use (the **"Agreement"**) is entered into electronically as of the date of acceptance (**"Effective Date"**) by and between:

**Disclosing Party:** AgentEros.AI (operated by AGENT EROS, ABN 029 512 917 800, hereinafter referred to as "the Company")

**Receiving Party:** The individual accepting this Agreement electronically as part of the Beta Program registration process (hereinafter referred to as "Beta Tester" or "you").

By clicking "I Accept" or by accessing and using the AgentEros.AI Beta platform, you acknowledge that you have read, understood, and agree to be bound by this Agreement in its entirety.

## 1. DEFINITIONS

For the purposes of this Agreement, the following terms shall have the meanings set out below:

**1.1 "Confidential Information"** means any and all non-public information, whether disclosed orally, visually, in writing, electronically, or by any other means, relating to AgentEros.AI, including but not limited to:

(a) The platform itself, including its features, functionality, user interface, user experience design, workflows, algorithms, AI coaching methodologies, prompts, system directives, and technical architecture;

(b) Screenshots, screen recordings, video captures, or any visual reproduction of any part of the platform;

(c) User stories, testimonials, experiences, results, or outcomes arising from use of the platform;

(d) Promotional materials, marketing strategies, content ideas, branding concepts, or go-to-market plans observed or discussed during the beta program;

(e) Business models, pricing strategies, partnership arrangements, product roadmaps, and planned features;

(f) Any bugs, errors, performance issues, or other feedback provided to or observed within the platform;

(g) The existence, nature, and details of the Beta Program itself;

(h) Any communications between the Beta Tester and the Company, including support messages, feedback sessions, surveys, and onboarding materials; and

(i) Any data, content, or information generated by, processed through, or displayed within the platform during your use.

**1.2 "Platform"** means the AgentEros.AI web application, including all associated services, tools, features, APIs, and content accessible at agenteros.ai or any related subdomains.

**1.3 "Beta Period"** means the duration of the Beta Program, commencing on the Effective Date and continuing until the Company publicly launches the Platform or terminates the Beta Program, whichever occurs first.

**1.4 "Public Launch"** means the date on which the Company makes the Platform generally available to the public, as officially announced by the Company.

## 2. CONFIDENTIALITY OBLIGATIONS

2.1 The Beta Tester agrees to hold all Confidential Information in strict confidence and shall not, without the prior written consent of the Company:

(a) Disclose, publish, or disseminate any Confidential Information to any third party, whether in person, online, or through any medium;

(b) Post, share, upload, or transmit any screenshots, screen recordings, descriptions, reviews, or commentary about the Platform on any social media platform, blog, forum, website, messaging service, podcast, video platform, or any other public or semi-public channel;

(c) Discuss, describe, or reference the features, functionality, design, user experience, or performance of the Platform with anyone who is not an authorised participant in the Beta Program;

(d) Create, prepare, or distribute any promotional content, marketing materials, testimonials, case studies, how-to guides, tutorials, or reviews based on the Platform or Beta experience;

(e) Use any Confidential Information for any purpose other than participating in the Beta Program and providing feedback to the Company; or

(f) Reverse engineer, decompile, disassemble, or otherwise attempt to derive the source code, algorithms, or underlying structure of the Platform.

2.2 The Beta Tester shall take all reasonable measures to protect the confidentiality of Confidential Information, using at minimum the same degree of care used to protect their own confidential information, but in no event less than reasonable care.

2.3 The Beta Tester shall not grant access to their beta account to any other person, and shall ensure that their login credentials remain secure and confidential.

## 3. PERMITTED DISCLOSURES

3.1 The obligations set out in Section 2 shall not apply to information that:

(a) Was already in the public domain at the time of disclosure, or becomes publicly available through no fault or action of the Beta Tester;

(b) Was already known to the Beta Tester prior to disclosure, as evidenced by written records predating the Effective Date;

(c) Is independently developed by the Beta Tester without reference to or use of any Confidential Information; or

(d) Is required to be disclosed by law, regulation, or court order, provided the Beta Tester gives the Company prompt written notice (where legally permissible) to allow the Company to seek a protective order.

## 4. POST-LAUNCH CONTENT RIGHTS

4.1 The Company encourages Beta Testers to prepare promotional content, testimonials, reviews, and social media posts about the Platform during the Beta Period for use after Public Launch.

4.2 Upon Public Launch, and subject to any content guidelines issued by the Company, Beta Testers will be permitted to publish and share their experiences, stories, screenshots, and reviews of the Platform.

4.3 Any content created during the Beta Period that references pre-release features, bugs, or functionality that does not exist in the publicly launched version should not be published without prior written consent from the Company.

4.4 The Company reserves the right to request removal of any post-launch content that contains inaccurate information about the Platform or that could reasonably be expected to cause reputational harm.

## 5. BETA TERMS OF USE

**5.1 Beta Access.** Access to the Beta Program is provided on an invitation-only basis at the sole discretion of the Company. The Company may revoke access at any time without notice or reason.

**5.2 "As-Is" Provision.** The Platform is provided during the Beta Period on an "as-is" and "as-available" basis. The Company makes no warranties, express or implied, regarding the Platform's functionality, reliability, availability, accuracy, or fitness for a particular purpose.

**5.3 Feedback.** The Beta Tester agrees to provide reasonable feedback regarding the Platform when requested. All feedback, suggestions, ideas, improvements, and bug reports provided by the Beta Tester shall become the exclusive property of the Company. The Company may use such feedback for any purpose without compensation or attribution to the Beta Tester.

**5.4 Data and Privacy.** The Beta Tester acknowledges that data collected during the Beta Period may be used to improve the Platform. The Company will handle personal data in accordance with its [Privacy Statement](/privacy). Beta data may be deleted, migrated, or reset at the Company's discretion during or after the Beta Period.

**5.5 Acceptable Use.** The Beta Tester agrees not to: (a) use the Platform for any unlawful purpose; (b) attempt to gain unauthorised access to the Platform's systems or infrastructure; (c) interfere with or disrupt the Platform's performance; (d) use automated tools, bots, or scrapers on the Platform; or (e) use the Platform to harass, abuse, or harm others.

**5.6 Account Termination.** The Company reserves the right to terminate a Beta Tester's access immediately, without notice, for any breach of this Agreement.

## 6. INTELLECTUAL PROPERTY

6.1 All right, title, and interest in and to the Platform, including all intellectual property rights, trade secrets, trademarks, and copyrights, remain the exclusive property of the Company.

6.2 This Agreement does not grant the Beta Tester any rights, title, or interest in the Platform beyond the limited right to access and use it during the Beta Period for testing purposes.

6.3 The AgentEros.AI name, logo, and branding are trademarks of the Company and may not be used without prior written consent.

## 7. LIMITATION OF LIABILITY

7.1 To the maximum extent permitted by applicable law, the Company shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from or related to the Beta Tester's participation in the Beta Program or use of the Platform.

7.2 The Company's total aggregate liability under this Agreement shall not exceed AUD $100.

7.3 Nothing in this Agreement excludes or limits liability that cannot be excluded or limited under applicable law, including the Australian Consumer Law.

## 8. REMEDIES FOR BREACH

8.1 The Beta Tester acknowledges that a breach of the confidentiality obligations in this Agreement may cause the Company irreparable harm for which monetary damages would be an inadequate remedy.

8.2 In the event of a breach or threatened breach, the Company shall be entitled to seek injunctive relief, specific performance, and any other equitable remedies available, in addition to any other rights and remedies available at law.

8.3 The Beta Tester shall be liable for any costs, losses, or damages (including reasonable legal fees) incurred by the Company as a result of the Beta Tester's breach of this Agreement.

8.4 The parties acknowledge and agree that the harm caused by a breach of the confidentiality obligations under this Agreement would be difficult to quantify. Accordingly, the Beta Tester agrees to pay the Company the sum of AUD $5,000 as liquidated damages for each individual breach of Section 2 of this Agreement. The parties agree that this amount represents a genuine pre-estimate of the minimum loss likely to be suffered by the Company as a result of such a breach and is not a penalty.

8.5 The payment of liquidated damages under Section 8.4 shall not limit the Company's right to seek injunctive relief, specific performance, or additional damages where actual losses exceed the liquidated damages amount.

## 9. TERM AND SURVIVAL

9.1 This Agreement is effective from the Effective Date and shall remain in force during the Beta Period.

9.2 The confidentiality obligations set out in Section 2 shall survive the termination or expiration of this Agreement for a period of three (3) years from the date of termination, or until such time as the Confidential Information enters the public domain through no fault of the Beta Tester, whichever occurs first.

9.3 Sections 3, 6, 7, 8, 9, 10, 11, and 12 shall survive termination of this Agreement.

## 10. GOVERNING LAW AND JURISDICTION

10.1 This Agreement shall be governed by and construed in accordance with the laws of the State of Victoria, Australia.

10.2 The parties submit to the non-exclusive jurisdiction of the courts of Victoria and any courts of appeal therefrom.

10.3 If any provision of this Agreement is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.

## 11. ELECTRONIC ACCEPTANCE

11.1 The parties agree that this Agreement may be accepted electronically and that such electronic acceptance constitutes a valid and binding signature under the Electronic Transactions Act 1999 (Cth) and applicable state legislation.

11.2 Before accessing the Beta Platform, the Beta Tester must individually confirm each of the following statements by selecting the corresponding checkbox, and then click the "I AGREE" button to complete acceptance.

11.3 The "I AGREE" button shall only become active once all six checkboxes above have been selected. Clicking "I AGREE" constitutes the Beta Tester's electronic signature and binding acceptance of this Agreement.

## 12. GENERAL PROVISIONS

**12.1 Entire Agreement.** This Agreement constitutes the entire agreement between the parties with respect to the subject matter hereof and supersedes all prior agreements, understandings, and communications, whether written or oral.

**12.2 Amendment.** The Company may amend this Agreement at any time by providing notice to the Beta Tester. Continued use of the Platform after such notice constitutes acceptance of the amended terms.

**12.3 Waiver.** No waiver of any provision of this Agreement shall be effective unless made in writing. A failure to enforce any provision shall not constitute a waiver of that provision.

**12.4 Assignment.** The Beta Tester may not assign or transfer this Agreement or any rights hereunder without the prior written consent of the Company. The Company may freely assign this Agreement.

**12.5 Notices.** All notices under this Agreement shall be sent to the email address associated with the Beta Tester's account, or to such other address as either party may designate in writing.

---

## ELECTRONIC ACCEPTANCE RECORD

| Field | Value |
|:------|:------|
| **Full Legal Name:** | ${fullLegalName} |
| **Email Address:** | ${email} |
| **Phone Number:** | ${phoneNumber} |
| **Date of Acceptance:** | ${timestampStr} |
| **Agreement Version:** | ${TC_VERSION} |
| **Method of Acceptance:** | Click-through ("I Agree" button) |

**For the Company:**

| Field | Value |
|:------|:------|
| **Entity:** | AGENT EROS |
| **ABN:** | 029 512 917 800 |
| **Authorised Representative:** | A. Asfar |
| **Contact Email:** | aasfar@agenteros.ai |

*--- END OF AGREEMENT ---*`;
};

// ==================== CHECKBOX DEFINITIONS ====================
const TC_CHECKBOXES = [
  { id: "read_understood", label: "I have read and understood this Agreement in its entirety" },
  { id: "agree_bound", label: "I agree to be bound by all terms and conditions herein" },
  { id: "age_capacity", label: "I am at least 18 years of age and have the legal capacity to enter into this Agreement" },
  { id: "record_retention", label: "I acknowledge that a record of my acceptance (including timestamp, IP address, and account details) will be retained by the Company" },
  { id: "info_accurate", label: "All information I have provided is true, accurate, and complete" },
  { id: "no_public_sharing", label: "I acknowledge that I am prohibited from publicly sharing, disclosing, or distributing any information, content, screenshots, or materials relating to Agent Eros until after Public Launch, as set out in Section 2 of this Agreement" },
];

// ==================== SIMPLE MARKDOWN RENDERER ====================
const renderMarkdown = (text) => {
  const lines = text.split("\n");
  const elements = [];
  let inTable = false;
  let tableRows = [];

  const flushTable = () => {
    if (tableRows.length > 0) {
      elements.push(
        <table key={`table-${elements.length}`} className="w-full border-collapse my-4 text-sm">
          <tbody>
            {tableRows.map((row, ri) => (
              <tr key={ri} className="border-b border-white/10">
                {row.map((cell, ci) => (
                  <td key={ci} className={`py-2 px-3 ${ci === 0 ? "font-semibold text-white/80 w-1/3" : "text-white/60"}`}>
                    <span dangerouslySetInnerHTML={{ __html: inlineFormat(cell) }} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
      tableRows = [];
    }
    inTable = false;
  };

  const inlineFormat = (str) => {
    return str
      .replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-cyan-400 underline underline-offset-2 hover:text-cyan-300">$1</a>');
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Table row
    if (line.startsWith("|") && line.endsWith("|")) {
      const cells = line.split("|").filter(Boolean).map(c => c.trim());
      // Skip separator rows
      if (cells.every(c => /^[-:]+$/.test(c))) continue;
      inTable = true;
      tableRows.push(cells);
      continue;
    }

    if (inTable) flushTable();

    // Headings
    if (line.startsWith("## ")) {
      elements.push(<h2 key={i} className="text-lg font-bold text-white mt-6 mb-3">{line.slice(3)}</h2>);
    } else if (line.startsWith("# ")) {
      elements.push(<h1 key={i} className="text-xl font-bold text-white mt-6 mb-3">{line.slice(2)}</h1>);
    } else if (line.startsWith("---")) {
      elements.push(<hr key={i} className="border-white/10 my-6" />);
    } else if (line.trim() === "") {
      elements.push(<div key={i} className="h-2" />);
    } else {
      elements.push(
        <p key={i} className="text-white/70 text-sm leading-relaxed mb-1">
          <span dangerouslySetInnerHTML={{ __html: inlineFormat(line) }} />
        </p>
      );
    }
  }
  if (inTable) flushTable();

  return elements;
};

// ==================== AUTH PAGE COMPONENT ====================
const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();
  const isCyber = theme === "agent_eros_1";
  const [isLoading, setIsLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({ name: "", email: "", password: "", confirmPassword: "" });
  
  // Referral code (from URL ?ref=XXX or user input)
  const [referralCode, setReferralCode] = useState("");
  const [referralFromUrl, setReferralFromUrl] = useState(false);
  const [referralValid, setReferralValid] = useState(null); // null=unchecked, true, false
  const [referralInfo, setReferralInfo] = useState(null);
  
  // Registration mode state
  const [registrationMode, setRegistrationMode] = useState("yes");
  const [emailLimitedDescription, setEmailLimitedDescription] = useState("");
  const [activeTab, setActiveTab] = useState("login");
  
  // Step 1: Access code modal state
  const [showAccessCodeModal, setShowAccessCodeModal] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  const [sponsorWelcome, setSponsorWelcome] = useState("");
  const [sponsorName, setSponsorName] = useState("");
  const [fullLegalName, setFullLegalName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [validatedEmail, setValidatedEmail] = useState("");
  const [validatingCode, setValidatingCode] = useState(false);
  
  // Step 2: T&C modal state
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
  const [checkboxes, setCheckboxes] = useState({});
  const [acceptingTerms, setAcceptingTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const scrollContainerRef = useRef(null);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [sendingReset, setSendingReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const allChecked = TC_CHECKBOXES.every(cb => checkboxes[cb.id] === true);
  const canAgree = hasScrolledToEnd && allChecked;
  
  // Fetch registration status on mount
  useEffect(() => {
    const fetchRegistrationStatus = async () => {
      try {
        const response = await axios.get(`${API}/auth/registration-status`);
        setRegistrationMode(response.data.registration_mode);
        setEmailLimitedDescription(response.data.email_limited_description || "");
      } catch (error) {
        console.error("Failed to fetch registration status:", error);
      }
    };
    fetchRegistrationStatus();
    
    // Pre-populate referral code from URL
    const refParam = searchParams.get("ref");
    if (refParam) {
      setReferralCode(refParam);
      setReferralFromUrl(true);
      // Validate it — if the URL-supplied code is invalid (or the
      // affiliate row was deactivated / never existed on this env),
      // unlock the input so the user can type a different code
      // instead of being stuck with an unusable pre-fill.
      axios.get(`${API}/affiliate/codes/validate?code=${encodeURIComponent(refParam)}`)
        .then(res => {
          setReferralValid(res.data.valid);
          if (res.data.valid) {
            setReferralInfo(res.data);
          } else {
            setReferralFromUrl(false);
          }
        })
        .catch(() => {
          setReferralValid(false);
          setReferralFromUrl(false);
        });
    }

    // Pre-populate name and email from URL (e.g. from {{reg_link}} in mailer)
    const nameParam = searchParams.get("name");
    const emailParam = searchParams.get("email");
    const tabParam = searchParams.get("tab");
    if (nameParam || emailParam) {
      setRegisterData(prev => ({
        ...prev,
        ...(nameParam ? { name: nameParam } : {}),
        ...(emailParam ? { email: emailParam } : {}),
      }));
    }
    if (tabParam === "register") setActiveTab("register");
  }, [searchParams]);

  // Scroll tracking for T&C modal
  const handleTermsScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (atBottom && !hasScrolledToEnd) {
      setHasScrolledToEnd(true);
    }
  }, [hasScrolledToEnd]);
  
  // Handle tab change. In email_limited (Beta) mode, the register
  // endpoint itself checks the waitlist — so we no longer gate the tab
  // switch. Beta T&C acceptance is enforced AFTER registration via the
  // /beta-terms page (see App.js ProtectedRoute tc-status guard).
  const handleTabChange = (newTab) => {
    if (newTab === "register") {
      if (registrationMode === "no") return;
    }
    setActiveTab(newTab);
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    setSendingReset(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail.trim() });
      setResetSent(true);
      toast.success("If an account exists with that email, a reset link has been sent.");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSendingReset(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Fraud Guardrails Phase 2 (A+C) — best-effort device fingerprint on login.
      let deviceFp = null;
      try { deviceFp = await computeDeviceFingerprint(); } catch { deviceFp = null; }
      const response = await authLogin(loginData.email, loginData.password, deviceFp);
      toast.success(isCyber ? "Welcome back to the signal" : "Welcome back, divine soul!");
      // Fix A: resolve destination BEFORE navigate, so Dashboard never
      // mounts for users still needing the wizard (avoids news-popup race).
      const dest = await resolveLoginDestination("/dashboard");
      navigate(dest, { state: { user: response.user }, replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerData.password !== registerData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (referralCode.trim() && referralValid === false) {
      toast.error("Invalid referral code. Please fix or remove it.");
      return;
    }
    setIsLoading(true);
    try {
      // Fraud Guardrails Phase 2 — best-effort device fingerprint
      let deviceFp = null;
      try { deviceFp = await computeDeviceFingerprint(); } catch { deviceFp = null; }
      const response = await authRegister(registerData.name, registerData.email, registerData.password, referralCode.trim() || null, deviceFp);
      if (response.welcome_message) {
        toast.success(response.welcome_message, { duration: 6000 });
      } else {
        toast.success(isCyber ? "Your journey begins" : "Your divine journey begins!");
      }
      // Beta T&C gate — every new user MUST accept Beta T&C before
      // reaching onboarding/dashboard. The /beta-terms page is behind
      // ProtectedRoute; the ProtectedRoute tc-status check will also
      // enforce this on any subsequent login attempt.
      navigate("/beta-terms", { state: { user: response.user }, replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };
  
  const validateReferralCode = async (value) => {
    if (!value.trim()) { setReferralValid(null); setReferralInfo(null); return; }
    try {
      const res = await axios.get(`${API}/affiliate/codes/validate?code=${encodeURIComponent(value.trim())}`);
      setReferralValid(res.data.valid);
      if (res.data.valid) {
        setReferralInfo(res.data);
        if (res.data.welcome_message) {
          setSponsorWelcome(res.data.welcome_message);
          setSponsorName(res.data.sponsor_name || "Your sponsor");
        } else {
          setSponsorWelcome("");
          setSponsorName("");
        }
      } else {
        setReferralInfo(null);
        setSponsorWelcome("");
        setSponsorName("");
      }
    } catch {
      setReferralValid(false);
      setReferralInfo(null);
    }
  };
  
  // Step 1: Validate access code + collect name/phone → open T&C modal
  const handleValidateAccessCode = async () => {
    if (!accessCode.trim()) { toast.error("Please enter your access code"); return; }
    if (!fullLegalName.trim()) { toast.error("Please enter your full legal name"); return; }
    if (!phoneNumber.trim()) { toast.error("Please enter your phone number"); return; }
    
    setValidatingCode(true);
    try {
      const response = await axios.post(`${API}/auth/validate-access-code`, {
        access_code: accessCode.trim()
      });
      
      if (response.data.valid) {
        setValidatedEmail(response.data.email);
        setRegisterData(prev => ({ ...prev, email: response.data.email, name: fullLegalName.trim() }));
        setShowAccessCodeModal(false);
        // Reset T&C state and open terms modal
        setHasScrolledToEnd(false);
        setCheckboxes({});
        setShowTermsModal(true);
        toast.success("Access Approve");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid access code");
    } finally {
      setValidatingCode(false);
    }
  };

  // Step 2: Accept T&C → save to DB → proceed to register tab
  const handleAcceptTerms = async () => {
    setAcceptingTerms(true);
    try {
      const agreementText = generateTcDocument({
        fullLegalName: fullLegalName.trim(),
        email: validatedEmail,
        phoneNumber: phoneNumber.trim(),
      });

      await axios.post(`${API}/auth/accept-terms`, {
        email: validatedEmail,
        full_legal_name: fullLegalName.trim(),
        phone_number: phoneNumber.trim(),
        agreement_version: TC_VERSION,
        agreement_text: agreementText,
        checkboxes: checkboxes,
      });

      setShowTermsModal(false);
      setTermsAccepted(true);
      setActiveTab("register");
      toast.success("Terms accepted. You may now complete registration.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save acceptance. Please try again.");
    } finally {
      setAcceptingTerms(false);
    }
  };

  const inputCls = isCyber
    ? "pl-10 bg-[#0a0a0f] border-cyan-500/30 text-white placeholder:text-white/30 focus:border-cyan-500"
    : "pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-divine-gold";

  const tcDocument = showTermsModal
    ? generateTcDocument({ fullLegalName: fullLegalName.trim(), email: validatedEmail, phoneNumber: phoneNumber.trim() })
    : "";

  return (
    <div className="min-h-screen bg-divine-black relative overflow-hidden flex items-center justify-center p-4">
      <Toaster position="top-center" richColors />
      
      {/* Background */}
      {isCyber ? (
        <>
          <div className="absolute inset-0 cyber-grid-bg opacity-20" />
          <div className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-gradient-to-tr from-magenta-500/20 to-cyan-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </>
      ) : (
        <>
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: "url('https://images.pexels.com/photos/2317685/pexels-photo-2317685.jpeg')" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-divine-black via-divine-black/90 to-divine-black" />
        </>
      )}

      {/* Auth Card */}
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8 flex flex-col items-center">
          <div className="mb-3">
            <SacredEmblem size={140} />
          </div>
          {isCyber ? (
            <>
              <h1 className="text-3xl text-white font-bold tracking-tight">Agent Eros</h1>
              <p className="text-white/60 mt-2">Tune the signal</p>
            </>
          ) : (
            <>
              <h1 className="font-cinzel text-3xl text-white">Agent Eros</h1>
              <p className="text-white/60 mt-2">Master the Art of Connection</p>
            </>
          )}
        </div>

        <div className={isCyber ? "cyber-card p-8 relative" : "glass-gold rounded-2xl p-8 relative"}>
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-white/5 rounded-lg p-1 mb-6">
              <TabsTrigger 
                data-testid="login-tab"
                value="login" 
                className={isCyber ? "data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-purple-500 data-[state=active]:text-white rounded-md font-semibold" : "font-cinzel data-[state=active]:bg-divine-red data-[state=active]:text-white rounded-md"}
              >
                Sign In
              </TabsTrigger>
              <TabsTrigger 
                data-testid="register-tab"
                value="register"
                disabled={registrationMode === "no"}
                className={`${isCyber ? "data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-purple-500 data-[state=active]:text-white rounded-md font-semibold" : "font-cinzel data-[state=active]:bg-divine-red data-[state=active]:text-white rounded-md"} ${registrationMode === "no" ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {registrationMode === "no" ? "Closed" : "Register"}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="space-y-4">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="text-white/80">Email</Label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
                    <Input id="login-email" type="email" placeholder="your@email.com" value={loginData.email} onChange={(e) => setLoginData({ ...loginData, email: e.target.value })} className={inputCls} required />
                  </div>
                </div>
                <div>
                  <Label htmlFor="login-password" className="text-white/80">Password</Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
                    <Input id="login-password" type="password" placeholder="••••••••" value={loginData.password} onChange={(e) => setLoginData({ ...loginData, password: e.target.value })} className={inputCls} required />
                  </div>
                </div>
                <Button
                  data-testid="login-submit-btn"
                  type="submit"
                  disabled={isLoading}
                  style={isCyber ? undefined : { background: "linear-gradient(135deg, #e9c98a, #c79b4f)", color: "#211505" }}
                  className={
                    isCyber
                      ? "w-full rounded-full py-3 bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white font-semibold tracking-widest border border-cyan-400/40 shadow-[0_0_24px_rgba(6,182,212,0.35)] hover:shadow-[0_0_34px_rgba(6,182,212,0.55)] hover:-translate-y-0.5 transition-all"
                      : "w-full rounded-full py-3 font-cinzel tracking-widest border border-[#d4af6a] shadow-[0_0_24px_rgba(212,175,106,0.25)] hover:shadow-[0_0_34px_rgba(212,175,106,0.45)] hover:-translate-y-0.5 transition-all"
                  }
                >
                  {isLoading ? "Signing in..." : "Enter"}
                </Button>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => { setShowForgotPassword(true); setResetSent(false); setForgotEmail(""); }}
                    className={`text-sm ${isCyber ? "text-cyan-400 hover:text-cyan-300" : "text-divine-gold hover:text-divine-gold/80"} transition-colors`}
                    data-testid="forgot-password-link"
                  >
                    Forgot your password?
                  </button>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="register" className="space-y-4">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="register-name" className="text-white/80">Name</Label>
                  <div className="relative mt-2">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
                    <Input id="register-name" type="text" placeholder="Your name" value={registerData.name} onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })} className={inputCls} required />
                  </div>
                </div>
                <div>
                  <Label htmlFor="register-email" className="text-white/80">
                    Email {validatedEmail && <span className="ml-2 text-xs text-green-400">(Verified)</span>}
                  </Label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
                    <Input id="register-email" type="email" placeholder="your@email.com" value={registerData.email} onChange={(e) => !validatedEmail && setRegisterData({ ...registerData, email: e.target.value })} readOnly={!!validatedEmail} className={`${inputCls} ${validatedEmail ? "opacity-70" : ""}`} required />
                  </div>
                </div>
                <div>
                  <Label htmlFor="register-password" className="text-white/80">Password</Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
                    <Input id="register-password" type="password" placeholder="••••••••" value={registerData.password} onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })} className={inputCls} required />
                  </div>
                </div>
                <div>
                  <Label htmlFor="register-confirm-password" className="text-white/80">Confirm Password</Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
                    <Input id="register-confirm-password" type="password" placeholder="••••••••" value={registerData.confirmPassword} onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })} className={inputCls} required />
                  </div>
                </div>
                <div>
                  <Label className="text-white/80">
                    Referral Code
                    {referralValid === true && <span className="ml-2 text-xs text-green-400">Valid</span>}
                    {referralValid === false && referralCode.trim() && <span className="ml-2 text-xs text-red-400">Invalid</span>}
                  </Label>
                  <div className="relative mt-2">
                    <Share2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
                    <Input
                      type="text"
                      value={referralCode}
                      readOnly={referralFromUrl}
                      onChange={(e) => { if (!referralFromUrl) { setReferralCode(e.target.value); setReferralValid(null); setReferralInfo(null); } }}
                      onBlur={() => { if (!referralFromUrl) validateReferralCode(referralCode); }}
                      placeholder="Enter affiliate code (optional)"
                      className={`${inputCls} ${referralFromUrl ? "opacity-70 cursor-default" : ""} ${referralValid === true ? "!border-green-500/50" : referralValid === false && referralCode.trim() ? "!border-red-500/50" : ""}`}
                      data-testid="register-access-code-display"
                    />
                  </div>
                  {referralInfo?.bonus_tokens > 0 && (
                    <p className="text-xs text-green-400 mt-1">Bonus: +{referralInfo.bonus_tokens} tokens on signup!</p>
                  )}
                  {sponsorWelcome && referralValid && (
                    <div className="mt-2" data-testid="register-sponsor-welcome">
                      <span className="block text-xs text-white/50 uppercase tracking-wider mb-1">A message from {sponsorName}</span>
                      <div className="p-3 rounded-lg" style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.25)" }}>
                        <p className="text-sm text-white/85 leading-relaxed">{sponsorWelcome}</p>
                      </div>
                    </div>
                  )}
                </div>
                <Button
                  data-testid="register-submit-btn"
                  type="submit"
                  disabled={isLoading}
                  style={isCyber ? undefined : { background: "linear-gradient(135deg, #e9c98a, #c79b4f)", color: "#211505" }}
                  className={
                    isCyber
                      ? "w-full rounded-full py-3 bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white font-semibold tracking-widest border border-cyan-400/40 shadow-[0_0_24px_rgba(6,182,212,0.35)] hover:shadow-[0_0_34px_rgba(6,182,212,0.55)] hover:-translate-y-0.5 transition-all"
                      : "w-full rounded-full py-3 font-cinzel tracking-widest border border-[#d4af6a] shadow-[0_0_24px_rgba(212,175,106,0.25)] hover:shadow-[0_0_34px_rgba(212,175,106,0.45)] hover:-translate-y-0.5 transition-all"
                  }
                >
                  {isLoading ? "Creating Account..." : isCyber ? "Begin" : "Begin Journey"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        {/* Back to Home link — placed below the card, under Forgot Password */}
        <div className="mt-5 text-center">
          <button
            type="button"
            data-testid="back-btn"
            onClick={() => navigate("/")}
            className={`inline-flex items-center justify-center gap-2 text-sm transition-colors ${
              isCyber
                ? "text-cyan-300/70 hover:text-cyan-200"
                : "text-white/60 hover:text-white"
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
        </div>
      </div>

      {/* ==================== MODAL 1: Access Code + Name + Phone ==================== */}
      <Dialog open={showAccessCodeModal} onOpenChange={setShowAccessCodeModal}>
        <DialogContent className={isCyber ? "bg-[#0a0a0f] border-cyan-500/30" : "bg-divine-charcoal border-divine-gold/30"}>
          <DialogHeader>
            <DialogTitle className={isCyber ? "text-cyan-400 flex items-center gap-2" : "text-divine-gold flex items-center gap-2"}>
              <Key className="w-5 h-5" />
              {emailLimitedDescription || "Beta Access"}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Enter your details and access code to proceed
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-white/80">Full Legal Name</Label>
              <div className="relative mt-2">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
                <Input
                  data-testid="access-code-legal-name"
                  type="text"
                  placeholder="Enter your full legal name"
                  value={fullLegalName}
                  onChange={(e) => setFullLegalName(e.target.value)}
                  className={isCyber ? "pl-10 bg-[#0a0a0f] border-cyan-500/30 text-white placeholder:text-white/30" : "pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30"}
                />
              </div>
            </div>

            <div>
              <Label className="text-white/80">Phone Number</Label>
              <div className="relative mt-2">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
                <Input
                  data-testid="access-code-phone"
                  type="tel"
                  placeholder="+61 400 000 000"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className={isCyber ? "pl-10 bg-[#0a0a0f] border-cyan-500/30 text-white placeholder:text-white/30" : "pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30"}
                />
              </div>
            </div>

            <div>
              <Label className="text-white/80">Email</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
                <Input
                  data-testid="access-code-input"
                  type="email"
                  placeholder="Enter your approved email"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleValidateAccessCode()}
                  className={isCyber ? "pl-10 bg-[#0a0a0f] border-cyan-500/30 text-white placeholder:text-white/30" : "pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30"}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Your email must be on the approved beta list.
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button
                data-testid="access-code-cancel-btn"
                variant="outline"
                onClick={() => setShowAccessCodeModal(false)}
                className="flex-1 border-white/20 text-white"
              >
                Cancel
              </Button>
              <Button
                data-testid="access-code-verify-btn"
                onClick={handleValidateAccessCode}
                disabled={validatingCode || !accessCode.trim() || !fullLegalName.trim() || !phoneNumber.trim()}
                className={isCyber ? "flex-1 bg-gradient-to-r from-cyan-500 to-purple-500 text-white" : "flex-1 bg-divine-gold text-black"}
              >
                {validatingCode ? "Validating..." : "Verify Code"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== MODAL 2: Terms & Conditions ==================== */}
      <Dialog open={showTermsModal} onOpenChange={(open) => { if (!open) { setShowTermsModal(false); setValidatedEmail(""); } }}>
        <DialogContent className={`${isCyber ? "bg-[#0a0a0f] border-cyan-500/30" : "bg-divine-charcoal border-divine-gold/30"} max-w-2xl max-h-[90vh] flex flex-col p-0`}>
          <DialogHeader className="px-6 pt-6 pb-3 shrink-0">
            <DialogTitle className={isCyber ? "text-cyan-400 flex items-center gap-2" : "text-divine-gold flex items-center gap-2"}>
              <FileText className="w-5 h-5" />
              Terms & Conditions — NDA & Beta Terms of Use
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Please read the entire agreement carefully. You must scroll to the end to proceed.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col flex-1 overflow-hidden px-6 pb-6">
            {/* Scrollable T&C content */}
            <div
              ref={scrollContainerRef}
              onScroll={handleTermsScroll}
              data-testid="terms-scroll-container"
              className={`flex-1 overflow-y-auto border rounded-lg p-4 mb-4 min-h-0 ${isCyber ? "border-cyan-500/20 bg-[#060610]" : "border-white/10 bg-black/30"}`}
              style={{ maxHeight: "45vh" }}
            >
              {renderMarkdown(tcDocument)}
            </div>

            {/* Scroll indicator */}
            {!hasScrolledToEnd && (
              <p data-testid="scroll-indicator" className="text-center text-xs text-yellow-400/80 mb-3 animate-pulse">
                Scroll down to read the full agreement
              </p>
            )}

            {/* Checkboxes — only visible after scrolling to end */}
            {hasScrolledToEnd && (
              <div data-testid="terms-checkboxes-section" className="space-y-2 mb-4">
                {TC_CHECKBOXES.map((cb) => (
                  <label
                    key={cb.id}
                    data-testid={`terms-checkbox-${cb.id}`}
                    className="flex items-start gap-3 cursor-pointer group p-2 rounded-lg hover:bg-white/5 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={!!checkboxes[cb.id]}
                      onChange={(e) => setCheckboxes(prev => ({ ...prev, [cb.id]: e.target.checked }))}
                      className={`mt-0.5 w-4 h-4 rounded shrink-0 ${isCyber ? "accent-cyan-500" : "accent-divine-gold"}`}
                    />
                    <span className="text-white/70 text-xs leading-relaxed group-hover:text-white/90 transition-colors">
                      {cb.label}
                    </span>
                  </label>
                ))}
              </div>
            )}

            {/* I AGREE button */}
            <div className="flex gap-3 shrink-0">
              <Button
                data-testid="terms-decline-btn"
                variant="outline"
                onClick={() => { setShowTermsModal(false); setValidatedEmail(""); }}
                className="flex-1 border-white/20 text-white"
              >
                Decline
              </Button>
              <Button
                data-testid="terms-agree-btn"
                onClick={handleAcceptTerms}
                disabled={!canAgree || acceptingTerms}
                className={`flex-1 ${canAgree
                  ? (isCyber ? "bg-gradient-to-r from-cyan-500 to-purple-500 text-white" : "bg-divine-gold text-black")
                  : "bg-gray-600 text-gray-400 cursor-not-allowed"
                }`}
              >
                {acceptingTerms ? "Saving..." : "I AGREE"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className={`${isCyber ? "bg-[#0a0a1a] border-cyan-500/30" : "bg-[#1a1a2e] border-divine-gold/30"} text-white max-w-md`}>
          <DialogHeader>
            <DialogTitle className={`text-lg ${isCyber ? "text-cyan-400" : "font-cinzel text-divine-gold"}`}>
              {resetSent ? "Check Your Email" : "Reset Password"}
            </DialogTitle>
          </DialogHeader>
          {resetSent ? (
            <div className="space-y-4 py-4">
              <div className={`p-4 rounded-lg ${isCyber ? "bg-cyan-500/10 border border-cyan-500/20" : "bg-divine-gold/10 border border-divine-gold/20"}`}>
                <Mail className={`w-8 h-8 mx-auto mb-3 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                <p className="text-center text-white/80 text-sm">
                  If an account exists for <strong className="text-white">{forgotEmail}</strong>, we've sent a password reset link. Please check your inbox and spam folder.
                </p>
              </div>
              <p className="text-center text-white/40 text-xs">The link expires in 1 hour.</p>
              <Button
                onClick={() => { setShowForgotPassword(false); setResetSent(false); }}
                className={isCyber ? "w-full bg-cyan-500 hover:bg-cyan-600 text-black" : "w-full bg-divine-gold hover:bg-divine-gold/90 text-black"}
                data-testid="forgot-password-done-btn"
              >
                Back to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4 py-2">
              <p className="text-white/60 text-sm">Enter the email address associated with your account and we'll send you a link to reset your password.</p>
              <div>
                <Label className="text-white/80 text-sm">Email Address</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
                  <Input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="your@email.com"
                    className={inputCls}
                    required
                    data-testid="forgot-password-email"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForgotPassword(false)}
                  className="flex-1 border-white/20 text-white hover:bg-white/5"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={sendingReset || !forgotEmail.trim()}
                  className={`flex-1 ${isCyber ? "bg-cyan-500 hover:bg-cyan-600 text-black" : "bg-divine-gold hover:bg-divine-gold/90 text-black"}`}
                  data-testid="forgot-password-submit-btn"
                >
                  {sendingReset ? "Sending..." : "Send Reset Link"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuthPage;
